import {
  type FC,
  type ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import {
  autocompletion,
  closeBrackets,
  closeBracketsKeymap,
  completionKeymap,
} from "@codemirror/autocomplete";
import {
  history,
  historyKeymap,
  indentWithTab,
  defaultKeymap as originalDefaultKeymap,
} from "@codemirror/commands";
import { html } from "@codemirror/lang-html";
import { javascript } from "@codemirror/lang-javascript";
import { json } from "@codemirror/lang-json";
import { liquid } from "@codemirror/lang-liquid";
import { markdown } from "@codemirror/lang-markdown";
import { sql } from "@codemirror/lang-sql";
import { xml } from "@codemirror/lang-xml";
import {
  bracketMatching,
  defaultHighlightStyle,
  indentOnInput,
  type LanguageSupport,
  syntaxHighlighting,
} from "@codemirror/language";
import { highlightSelectionMatches, searchKeymap } from "@codemirror/search";
import { Compartment, EditorState, type Extension } from "@codemirror/state";
import {
  EditorView,
  keymap,
  lineNumbers,
  placeholder,
  type ViewUpdate,
} from "@codemirror/view";
import {
  Box,
  type BoxProps,
  IconButton,
  Portal,
  Row,
  SparkleIcon,
  Tooltip,
  useToast,
} from "@hightouch/htui";

import { canFormatLanguage, formatCode } from "src/utils/format-code";

import css from "./editor.module.css";
import { highlightErroredLine } from "./highlight-errored-line";

const erroredLineCompartment = new Compartment();

// Disable ⌘ + Enter shortcut to insert a blank line, because it
// also triggers a preview in the SQL editor
const defaultKeymap = originalDefaultKeymap.filter((keyBinding) => {
  return keyBinding.key !== "Mod-Enter";
});

export type Language =
  | "sql"
  | "json"
  | "xml"
  | "liquid"
  | "javascript"
  | "html"
  | "markdown"
  | LanguageSupport;

const initializeLanguage = (
  language: Language,
  languageAsLiquidBase?: boolean,
): LanguageSupport => {
  if (languageAsLiquidBase == true) {
    return liquid({ base: initializeLanguage(language, false) });
  } else {
    switch (language) {
      case "sql":
        return sql();
      case "json":
        return json();
      case "xml":
        return xml();
      case "liquid":
        return liquid();
      case "javascript":
        return javascript();
      case "html":
        return html();
      case "markdown":
        return markdown();
      default:
        return language;
    }
  }
};

// Compartments allow replacing specific extensions without affecting all the other ones
// See https://codemirror.net/6/docs/ref/#state.Compartment
const languageCompartment = new Compartment();
const placeholderCompartment = new Compartment();
const readOnlyCompartment = new Compartment();

export interface Props {
  /**
   * Line number to highlight with a red background, in case there's an error
   */
  highlightErroredLine?: number;

  /**
   * Determines if user can edit the code
   */
  readOnly?: boolean;

  /**
   * Code to show in the editor
   */
  value: string;

  /**
   * Language of the code
   */
  language?: Language;

  /**
   * Indication to use the aforementioned language as
   * the "base" language of liquidjs
   */
  languageAsLiquidBase?: boolean;

  /**
   * Text to show when there's no code
   */
  placeholder?: string;

  /**
   * CodeMirror extensions
   */
  extensions?: Extension[];

  /**
   * Callback for when editor is initialized
   */
  onInit?: (options: { view: EditorView }) => void;

  /**
   * Callback for when the code is changed
   */
  onChange?: ChangeCallback;

  /**
   * Minimum height of the editor
   * @default 200px
   */
  minHeight?: string;

  /**
   * Override the background color of the editor
   */
  bg?: BoxProps["bg"];

  /**
   * Show/hide line numbers
   */
  showLineNumbers?: boolean;

  actions?: ReactNode;
}

type ChangeCallback = (value: string) => void;

export const Editor: FC<Props> = ({
  highlightErroredLine: highlightErroredLineNumber,
  readOnly = false,
  value,
  language,
  languageAsLiquidBase,
  placeholder: placeholderText,
  extensions,
  onInit,
  onChange,
  minHeight = "200px",
  bg,
  showLineNumbers = true,
  actions,
}) => {
  const container = useRef<HTMLDivElement>(null);
  const [view, setView] = useState<EditorView | undefined>();

  const autocompleteContainer = useRef<HTMLDivElement>(null);

  // Store the reference to the latest `onChange` function, so that `updateListener`
  // always calls the latest `onChange` value without triggering `useEffect` update
  // and forcing CodeMirror to re-bind itself
  const lazyOnChange = useRef<ChangeCallback | undefined>(onChange);
  // Track the latest `value` prop so the updateListener can skip onChange when
  // the document matches the current prop (prevents duplicate calls when we
  // programmatically sync the editor). Without this ref the closure captures
  // only the *initial* value, so user edits that restore the original text
  // would silently not fire onChange.
  const latestValue = useRef(value);

  useLayoutEffect(() => {
    lazyOnChange.current = onChange;
  }, [onChange]);

  useLayoutEffect(() => {
    latestValue.current = value;
  }, [value]);

  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  useEffect(() => {
    if (!container.current) {
      return undefined;
    }

    const updateListener = EditorView.updateListener.of(
      (viewUpdate: ViewUpdate) => {
        if (viewUpdate.docChanged) {
          const newValue = viewUpdate.state.doc.toString();

          if (
            typeof lazyOnChange.current === "function" &&
            latestValue.current !== newValue
          ) {
            lazyOnChange.current(newValue);
          }
        }
      },
    );

    const state = EditorState.create({
      doc: value,
      extensions: [
        ...(showLineNumbers ? [lineNumbers()] : []),
        highlightSelectionMatches(),
        indentOnInput(),
        syntaxHighlighting(defaultHighlightStyle, { fallback: true }),
        bracketMatching(),
        closeBrackets(),
        autocompletion(),
        history(),
        updateListener,
        keymap.of([
          ...closeBracketsKeymap,
          ...defaultKeymap,
          ...historyKeymap,
          ...searchKeymap,
          ...completionKeymap,
          indentWithTab,
        ]),
        ...(language
          ? [
              languageCompartment.of(
                initializeLanguage(language, languageAsLiquidBase),
              ),
            ]
          : []),
        placeholderCompartment.of(placeholder("")),
        readOnlyCompartment.of(EditorState.readOnly.of(readOnly)),
        erroredLineCompartment.of(
          highlightErroredLine(highlightErroredLineNumber),
        ),
        EditorView.contentAttributes.of({
          // Aria-label is required for accessibility.
          "aria-label": language
            ? `${typeof language === "string" ? language.toUpperCase() : "Code"} editor`
            : "Code editor",
        }),
        ...(extensions ?? []),
      ],
    });

    const view = new EditorView({
      state,
      parent: container.current,
    });

    // Set tabindex on the scrollable region (cm-scroller element) - accessibility requirement.
    view.scrollDOM.setAttribute("tabindex", "0");

    setView(view);

    // Move the autocomplete element to the portal
    const moveAutocompleteToPortal = () => {
      const autocompleteElement = document.querySelector(
        ".cm-tooltip-autocomplete",
      );

      if (autocompleteElement && autocompleteContainer.current) {
        const cursorPos = view.state.selection.main.head;
        const coords = view.coordsAtPos(cursorPos);

        if (coords) {
          autocompleteContainer.current.style.position = "absolute";
          autocompleteContainer.current.style.left = "0";
          autocompleteContainer.current.style.top = `${coords.top}px`;
          autocompleteContainer.current.style.zIndex = "1";
          // TODO: fix height calculation of autocompleteElement
        }

        autocompleteContainer.current.appendChild(autocompleteElement);
      }
    };

    // Observe the changes in the editor view to relocate autocomplete element
    const autocompleteObserver = new MutationObserver(moveAutocompleteToPortal);
    autocompleteObserver.observe(view.dom, { childList: true, subtree: true });

    return () => {
      view.destroy();
      setView(undefined);
      autocompleteObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!view) {
      return;
    }

    view.dispatch({
      effects: erroredLineCompartment.reconfigure(
        highlightErroredLine(highlightErroredLineNumber),
      ),
    });
  }, [view, highlightErroredLineNumber]);

  useEffect(() => {
    if (view && typeof onInit === "function") {
      onInit({ view });
    }
  }, [view, onInit]);

  useEffect(() => {
    if (!view) {
      return;
    }

    if (language) {
      view.dispatch({
        effects: languageCompartment.reconfigure(
          initializeLanguage(language, languageAsLiquidBase),
        ),
      });
    }
  }, [view, language, languageAsLiquidBase]);

  useEffect(() => {
    if (!view || !placeholderText) {
      return;
    }

    view.dispatch({
      effects: placeholderCompartment.reconfigure(placeholder(placeholderText)),
    });
  }, [view, placeholderText]);

  useEffect(() => {
    if (!view) {
      return;
    }

    view.dispatch({
      effects: readOnlyCompartment.reconfigure(
        EditorState.readOnly.of(readOnly),
      ),
    });
  }, [view, readOnly]);

  useEffect(() => {
    if (!view) {
      return;
    }

    const currentValue = view.state.doc.toString();

    // Update editor content only when `value` is updated, except when it's
    // updated as a result of this component calling `onChange` as user is typing
    if (value !== currentValue) {
      view.dispatch({
        changes: {
          from: 0,
          to: currentValue.length,
          insert: value ?? "",
        },
      });
    }
  }, [view, value]);

  const { toast } = useToast();

  // Liquid-base content is a template, not parseable XML/JSON: the xml
  // formatter silently corrupts it (truncates at `<` inside Liquid tags,
  // auto-closes intentionally unbalanced fragments), so never offer Beautify.
  const showBeautify =
    canFormatLanguage(language) &&
    !languageAsLiquidBase &&
    !readOnly &&
    typeof onChange === "function";

  const handleBeautify = () => {
    const { output, error } = formatCode(value, language);
    if (output !== null) {
      onChange?.(output);
    } else {
      toast({
        id: "beautify-editor",
        title: "Couldn't format the code",
        message: error ?? undefined,
        variant: "warning",
      });
    }
  };

  return (
    <Box
      display="flex"
      flexDir="column"
      ref={container}
      className={css.editor}
      height="100%"
      width="100%"
      flex={1}
      overflow="hidden"
      minHeight={minHeight}
      bg={bg || (readOnly ? "gray.50" : "white")}
      sx={
        bg || readOnly
          ? { "& .cm-gutters": { bg: bg || (readOnly ? "gray.50" : "white") } }
          : undefined
      }
      pl={showLineNumbers ? undefined : 2}
      position="relative"
    >
      {(showBeautify || actions) && (
        <Row
          align="center"
          gap={1}
          position="absolute"
          top={1}
          right={1}
          zIndex={1}
        >
          {actions}
          {showBeautify && (
            <Tooltip message="Beautify">
              <IconButton
                aria-label="Beautify"
                icon={SparkleIcon}
                size="sm"
                variant="secondary"
                onClick={handleBeautify}
              />
            </Tooltip>
          )}
        </Row>
      )}
      <Portal>
        <Box ref={autocompleteContainer} />
      </Portal>
    </Box>
  );
};
